import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyQuery, useMutation } from '@apollo/react-hooks';
import { getOr, get, isEmpty } from 'lodash/fp';
import NumberFormat from 'react-number-format';
import {
  Button, Checkbox, HStack, useToast,
} from '@chakra-ui/react';
import {
  FontBold, DividerLine,
} from '../../Components/Auth/Layout';
import {
  FETCH_EVENT_DETAIL,
  PAYMENT_USER_AGAINST_REGISTRATION_OPTION,
  UNPAID_USER_AGAINST_REGISTRATION_OPTION,
  PAID_USER_AGAINST_REGISTRATION_OPTION,
  FETCH_REGISTRATION_OPTION_DETAIL,
} from '../../GraphQL/Queries';
import { BULK_REMOVE_UNPAID_USERS } from '../../GraphQL/Mutations';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import Loader from '../../Components/Loader';
import GenericAlert from '../../Components/GenericAlert';
import useGraphQLErrorExtractor from '../../Hooks/useGraphQLErrorExtractor';
import useCurrentUser from '../../Hooks/userCurrentUser';
import { EVENT_TITLE } from '../../Constants/AppConstants';
import { Provider } from '../../Chakra/provider';

const PaymentsByOption = () => {
  const {
    currentUser,
    loading: userLoading,
    error: userError,
  } = useCurrentUser(true);
  const { id } = useParams();
  const { optionId } = useParams();
  const extractError = useGraphQLErrorExtractor();
  const toast = useToast();

  const [isRemoving, setIsRemoving] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());

  const [fetchEventDetail, { data, loading, error }] = useLazyQuery(FETCH_EVENT_DETAIL);
  const event = getOr({}, 'fetchEventDetail', data);

  const [paymentUserAgainstRegistrationOption,
    { data: dataPayment, loading: paymentLoading, error: paymentError }] = useLazyQuery(
    PAYMENT_USER_AGAINST_REGISTRATION_OPTION, { fetchPolicy: 'network-only' },
  );
  const paymentInformation = getOr({}, 'paymentUserAgainstRegistrationOption', dataPayment);

  const [unpaidUserAgainstRegistrationOption,
    { data: dataUnpaid, loading: upaidLoading, error: unpaidError }] = useLazyQuery(
    UNPAID_USER_AGAINST_REGISTRATION_OPTION, { fetchPolicy: 'network-only' },
  );
  const unpaidUsers = getOr([], 'unpaidUserAgainstRegistrationOption', dataUnpaid);

  const [paidUserAgainstRegistrationOption,
    { data: dataPaid, loading: paidLoading, error: paidError }] = useLazyQuery(
    PAID_USER_AGAINST_REGISTRATION_OPTION,
  );
  const paidUsers = getOr([], 'paidUserAgainstRegistrationOption', dataPaid);

  const [fetchRegistrationOptionDetail, { data: dataFetchRegistrationOptionDetail }] = useLazyQuery(
    FETCH_REGISTRATION_OPTION_DETAIL,
  );

  const registrationOption = getOr({}, 'fetchRegistrationOptionDetail', dataFetchRegistrationOptionDetail);

  const [bulkRemoveUnpaidUsers, { loading: bulkRemoveLoading }] = useMutation(BULK_REMOVE_UNPAID_USERS);

  useHeaderComponentsMutation({
    title: registrationOption.title,
    backLink: `/events/${id}/payment-report`,
    components: [{ key: EVENT_TITLE, value: event.title }],
  });

  useEffect(() => {
    if (currentUser && id) {
      fetchEventDetail({
        variables: {
          id: parseInt(id, 10),
        },
      });
    }
  }, [currentUser, fetchEventDetail, id]);

  useEffect(() => {
    if (currentUser && id) {
      paymentUserAgainstRegistrationOption({
        variables: {
          eventId: parseInt(id, 10),
          registrationOptionId: parseInt(optionId, 10),
        },
      });
    }
  }, [currentUser, paymentUserAgainstRegistrationOption, id, optionId]);

  useEffect(() => {
    if (currentUser && id) {
      unpaidUserAgainstRegistrationOption({
        variables: {
          eventId: parseInt(id, 10),
          registrationOptionId: parseInt(optionId, 10),
        },
      });
    }
  }, [currentUser, unpaidUserAgainstRegistrationOption, id, optionId]);

  useEffect(() => {
    if (currentUser && id) {
      paidUserAgainstRegistrationOption({
        variables: {
          eventId: parseInt(id, 10),
          registrationOptionId: parseInt(optionId, 10),
        },
      });
    }
  }, [currentUser, paidUserAgainstRegistrationOption, id, optionId]);

  useEffect(() => {
    if (currentUser) {
      fetchRegistrationOptionDetail({
        variables: {
          id: parseInt(optionId, 10),
        },
      });
    }
  }, [currentUser, fetchRegistrationOptionDetail, optionId]);

  const refetchOption = () => {
    const variables = {
      eventId: parseInt(id, 10),
      registrationOptionId: parseInt(optionId, 10),
    };
    unpaidUserAgainstRegistrationOption({ variables });
    paymentUserAgainstRegistrationOption({ variables });
  };

  const unpaidUserIds = unpaidUsers.map((u) => u.id);
  const allSelected = unpaidUserIds.length > 0 && unpaidUserIds.every((i) => selectedUserIds.has(i));
  const someSelected = unpaidUserIds.some((i) => selectedUserIds.has(i)) && !allSelected;

  const toggleUser = (userId) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      const allOn = unpaidUserIds.length > 0 && unpaidUserIds.every((i) => next.has(i));
      unpaidUserIds.forEach((i) => (allOn ? next.delete(i) : next.add(i)));
      return next;
    });
  };

  const cancelRemoving = () => {
    setIsRemoving(false);
    setSelectedUserIds(new Set());
  };

  const handleBulkRemove = async () => {
    try {
      const { data: mutationData } = await bulkRemoveUnpaidUsers({
        variables: {
          input: {
            userIdsToRemove: Array.from(selectedUserIds),
            eventId: parseInt(id, 10),
            checkPaid: true,
          },
        },
      });

      const warnings = mutationData?.bulkRemoveUnpaidUsers?.warnings ?? [];
      if (warnings.length > 0) {
        toast({
          title: 'Partial Success',
          description: warnings.join(', '),
          status: 'warning',
          position: 'top',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Success',
          description: 'Selected users have been removed and notified',
          status: 'success',
          position: 'top',
          duration: 5000,
          isClosable: true,
        });
      }

      refetchOption();
      cancelRemoving();
    } catch (err) {
      toast({
        title: 'Error',
        description: extractError(err),
        status: 'error',
        position: 'top',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading || userLoading || paymentLoading || upaidLoading || paidLoading) {
    return <Loader />;
  }
  if (error || userError || paymentError || unpaidError || paidError) {
    return <GenericAlert>{extractError(error)}</GenericAlert>;
  }

  return (
    <>
      <div className="mt-4" />
      <div className="row">
        <div className="col-12 mx-auto px-4">
          <div className="row">
            <div className="col-4">
              <FontBold>
                <p className="mt-4 text-center">
                  Unpaid
                </p>
              </FontBold>
              <h5 className="text-center">
                {get('unpaid_user_count', paymentInformation)}
              </h5>
            </div>
            <div className="col-4">
              <FontBold>
                <p className="mt-4 text-center">
                  Paid
                </p>
              </FontBold>
              <h5 className="text-center">
                {get('paid_user_count', paymentInformation)}
              </h5>
            </div>
            <div className="col-4">
              <FontBold>
                <p className="mt-4 text-center">
                  Total
                </p>
              </FontBold>
              <h5 className="text-center">
                {get('total_user', paymentInformation)}
              </h5>
            </div>
          </div>
          <div className="pt-3" />
          {!isEmpty(unpaidUsers)
            ? (
              <>
                <HStack alignItems="center" spacing={3} mb={2}>
                  <FontBold>
                    <p className="mb-0">
                      {`Unpaid (${unpaidUsers.length})`}
                    </p>
                  </FontBold>
                  {isRemoving ? (
                    <>
                      <Checkbox
                        isChecked={allSelected}
                        isIndeterminate={someSelected}
                        onChange={toggleAll}
                      >
                        Select all
                      </Checkbox>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={cancelRemoving}
                        disabled={bulkRemoveLoading}
                      >
                        Cancel
                      </Button>
                      <Button
                        colorScheme="red"
                        size="sm"
                        isDisabled={selectedUserIds.size === 0}
                        isLoading={bulkRemoveLoading}
                        onClick={handleBulkRemove}
                      >
                        {`Confirm Remove (${selectedUserIds.size})`}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      colorScheme="red"
                      size="sm"
                      onClick={() => setIsRemoving(true)}
                    >
                      Bulk Remove
                    </Button>
                  )}
                </HStack>

                {unpaidUsers.map((item) => (
                  <React.Fragment key={item.id}>
                    <div className="row pl-2 my-2 align-items-center">
                      {isRemoving && (
                        <div className="col-1">
                          <Checkbox
                            isChecked={selectedUserIds.has(item.id)}
                            onChange={() => toggleUser(item.id)}
                          />
                        </div>
                      )}
                      <div className={isRemoving ? 'col-md-9 col-8' : 'col-md-10 col-9'}>
                        {get('firstname', item)}
                        {' '}
                        {get('lastname', item)}
                        {' '}
                        (
                        {get('code', item)}
                        )
                      </div>
                      <div className="col-md-2 col-3 pl-5 float-right">
                        <NumberFormat value={get('amount', item) / 100} displayType="text" thousandSeparator decimalScale={2} />
                        {' '}
                        {get('defaultCurrency', item)}
                      </div>
                    </div>
                    <DividerLine className="my-3" />
                  </React.Fragment>
                ))}
              </>
            )
            : null}
          <div className="pt-3" />
          {!isEmpty(paidUsers)
            ? (
              <>
                <FontBold>
                  <p>
Paid (
                    {paidUsers.length}
)
                  </p>
                </FontBold>
                {paidUsers.map((item) => (
                  <>
                    <div className="row pl-2 my-2">
                      <div className="col-md-10 col-9 ">
                        {get('firstname', item)}
                        {' '}
                        {get('lastname', item)}
                        {' '}
(
                        {get('code', item)}
)
                      </div>
                      <div className="col-md-2 col-3 pl-5  float-right">
                        <NumberFormat value={get('amount', item) / 100} displayType="text" thousandSeparator decimalScale={2} />
                        {' '}
                        {get('defaultCurrency', item)}
                      </div>
                    </div>
                    <DividerLine className="my-3" />
                  </>
                ))}
              </>
            )
            : null}
        </div>
      </div>
    </>
  );
};

export default () => (
  <Provider>
    <PaymentsByOption />
  </Provider>
);
